import React, { ComponentProps, ReactNode } from "react";
import { ModalProps } from "react-bootstrap/Modal";
import Image from "react-bootstrap/Image";

import "./CollectGemWinHprModal.scss";
import AlarmIcon from "../../../icons/AlarmIcon";
import FilledButton from "../../FilledButton/FilledButton";
import ImaageCloseIcon from "../../../../images/close-icon-purple.svg";
import GhostButton from "../../GhostButton/GhostButton";
import Gem from "../../../Gem/Gem";
import HyperCoin from "../../../HyperCoin/HyperCoin";
import CustomProgressBar from "../../../CustomProgressBar/CustomProgressBar";
import { formatCurrency } from "../../../../utils/helpers";
import DownloadAppPrompt, {
  PROMPT_DOWNLOAD_SCENARIOS,
} from "../../../DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../../../hooks/useDisclosure";
import useNotifications from "../../../../hooks/useNotifications";
import BottomModal from "../../../BottomModal/BottomModal";
import useAuth from "../../../../hooks/useAuth";
import EnableNotificationsDialog from "../../../notification-flow/EnableNotificationsDialog/EnableNotificationsDialog";
import { useTranslation } from "react-i18next";
import useHprFiatFormatter from "../../../../hooks/useHprFiatFormatter";

const DAILY_HPR_PRIZE = 1000;

interface CollectItemProps extends ComponentProps<"div"> {
  gem: number;
  chance: number;
  hpr?: number;
  progressLabel?: ReactNode;
}

function CollectItem({
  gem,
  chance,
  progressLabel,
  hpr = 1000,
  ...restProps
}: CollectItemProps) {
  const { t } = useTranslation("common");
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();

  const displayPrize = isFiatDisplay ? formatHprToFiatValue(hpr, 5) : hpr;

  return (
    <div className="collect-item" {...restProps}>
      <p className="title">
        {t("collect")} <Gem width={13} height={12} /> {formatCurrency(gem)}/
        {t("week")}
      </p>
      <div className="content">
        <CustomProgressBar
          value={chance}
          bgColor="#F5F0FF"
          labelPlacement="right"
        >
          {progressLabel || (
            <p className="chance">
              {chance}% {t("opportunity")}
            </p>
          )}
        </CustomProgressBar>
        <p className="prize">
          {!isFiatDisplay && <HyperCoin height={12} width={12} />}
          {displayPrize}
        </p>
      </div>
    </div>
  );
}

function CollectGemWinHprModal({ onHide, show, ...restProps }: ModalProps) {
  const { isLoggedIn: userIsLoggedIn } = useAuth();
  const { notificationsAreEnabled, ...notifications } = useNotifications();
  const { t } = useTranslation("common");
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();

  const ctaText = notificationsAreEnabled ? t("earn_gem") : t("notify_me");
  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();
  const {
    isOpen: isNotificationsPromptOpen,
    onOpen: openNotificationsPrompt,
    onClose: closeNotificationsPrompt,
  } = useDisclosure();

  const handleNotifyMe = () => {
    onHide && onHide();
    notificationsAreEnabled
      ? openDownloadAppPrompt()
      : openNotificationsPrompt();
  };

  const displayDailyPrize = isFiatDisplay
    ? formatHprToFiatValue(DAILY_HPR_PRIZE, 5)
    : null;

  return (
    <>
      <BottomModal className="CollectGemWinHprModal" show={show} {...restProps}>
        <div className="modal-content pt-2 pb-5 px-3">
          <div className="top-row position-relative d-flex justify-content-center mb-0">
            <div className="row-hero mt-5 mb-2">
              <span className="gem-hpr">
                <Gem width={46} height={41} className="gem" />
                <HyperCoin width={35} height={35} className="hpr" />
              </span>
              <span className="coming-soon">{t("coming_soon")}</span>
            </div>

            <GhostButton
              aria-label="close-modal"
              className="close-btn position-absolute"
              onClick={onHide}
            >
              <Image src={ImaageCloseIcon} alt="close" width={30} height={30} />
            </GhostButton>
          </div>
          <h2 className="modal-heading">
            {isFiatDisplay
              ? t("collect_gem_win_reward")
              : t("collect_gem_win_hpr")}
          </h2>
          <p className="subtext">
            {t("collect")} <Gem />{" "}
            {isFiatDisplay
              ? t("gem_and_stand_a_opportunity_to_win")
              : t("gem_and_get_the_opportunity_to_win")}{" "}
            {!isFiatDisplay && <HyperCoin />}{" "}
            {isFiatDisplay
              ? `${displayDailyPrize} ${t("daily_reward")}`
              : t("daily_hpr_count", { count: DAILY_HPR_PRIZE })}
          </p>
          <div className="what-you-can-get">
            <h5>{t("see_what_you_can_get")}</h5>
            <CollectItem gem={100} chance={1} />
            <CollectItem gem={1000} chance={10} />
            <CollectItem
              gem={10000}
              chance={100}
              progressLabel={
                <p className="chance" style={{ color: "white" }}>
                  {t("nearly_hundred_percent_opportunity")}
                </p>
              }
              style={{ marginBottom: 0 }}
            />
          </div>
          <FilledButton style={{ height: 52 }} onClick={handleNotifyMe}>
            {!notificationsAreEnabled && <AlarmIcon fill="white" />}
            <span className="ms-2">{ctaText}</span>
          </FilledButton>
        </div>
      </BottomModal>

      <DownloadAppPrompt
        isOpen={isDownloadAppPromptOpen}
        onOpen={openDownloadAppPrompt}
        onClose={closeDownloadAppPrompt}
        promptDownloadScenario={
          userIsLoggedIn
            ? PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.OTHERS
            : PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.OTHERS
        }
      />
      <EnableNotificationsDialog
        handleNotifyMe={notifications.handleNotifyMe}
        enableNotificationsStep={notifications.enableNotificationsStep}
        isOpen={isNotificationsPromptOpen}
        onClose={closeNotificationsPrompt}
      />
    </>
  );
}

export default CollectGemWinHprModal;
